import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AlertCircle, ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from "@sportscanner/ui";
import { resetPasswordSchema } from "@sportscanner/schemas";
import AuthLayout from "@/components/auth-layout";
import { resetPassword } from "@/lib/auth-client";

const PASSWORD_REGEX_UPPER = /[A-Z]/;
const PASSWORD_REGEX_NUMBER = /[0-9]/;

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return null;
  if (!PASSWORD_REGEX_UPPER.test(pw)) return null;
  if (!PASSWORD_REGEX_NUMBER.test(pw)) return null;
  return pw;
}

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordWeak, setPasswordWeak] = useState(false);

  const passwordValid = validatePassword(newPassword) !== null;
  const isDisabled =
    !newPassword || !confirmPassword || isLoading || !passwordValid;

  useEffect(() => {
    if (confirmPassword && newPassword !== confirmPassword) {
      setPasswordMismatch(true);
    } else {
      setPasswordMismatch(false);
    }
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    if (newPassword.length > 0 && !passwordValid) {
      setPasswordWeak(true);
    } else {
      setPasswordWeak(false);
    }
  }, [newPassword, passwordValid]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled || passwordMismatch) return;

    const parsed = resetPasswordSchema.safeParse({
      password: newPassword,
      confirmPassword,
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);

    try {
      const result = await resetPassword({
        newPassword: parsed.data.password,
        token,
      });

      if (result?.error) {
        setErrorCode("TOKEN_INVALID");
        return;
      }

      toast.success(t("auth.resetPassword.successToast"));
      navigate("/login");
    } catch {
      setErrorCode("INTERNAL_ERROR");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Back */}
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Link>

        {/* Heading */}
        <div className="space-y-1">
          <h1 className="font-heading text-2xl font-bold text-neutral-800">
            {t("auth.resetPassword.heading")}
          </h1>
          <p className="text-sm text-neutral-500">
            {t("auth.resetPassword.description")}
          </p>
        </div>

        {/* Error */}
        {errorCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
          </Alert>
        )}

        {/* Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-sm text-neutral-600">
              {t("auth.resetPassword.newPasswordLabel")}
            </Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNew ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("auth.resetPassword.newPasswordPlaceholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                className={`rounded-lg border-neutral-200 focus:border-primary-500 pr-10 ${passwordWeak ? "border-red-400" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
                tabIndex={-1}
              >
                {showNew ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {passwordWeak && (
              <p className="text-xs text-red-500">
                {t("auth.resetPassword.passwordRules")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="confirm-password"
              className="text-sm text-neutral-600"
            >
              {t("auth.resetPassword.confirmPasswordLabel")}
            </Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("auth.resetPassword.confirmPasswordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                className={`rounded-lg border-neutral-200 focus:border-primary-500 pr-10 ${passwordMismatch ? "border-red-400" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
                tabIndex={-1}
              >
                {showConfirm ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {passwordMismatch && (
              <p className="text-xs text-red-500">
                {t("auth.resetPassword.passwordMismatch")}
              </p>
            )}
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={isDisabled || passwordMismatch}
          className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.resetPassword.submitting")}
            </>
          ) : (
            t("auth.resetPassword.submit")
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
